import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface ClientFollowUp {
  id: string;
  client_id: string;
  employee_id: string;
  follow_up_type: string;
  status: string;
  scheduled_date: string | null;
  completed_date: string | null;
  priority: string;
  notes: string | null;
  outcome: string | null;
  next_action: string | null;
  next_action_date: string | null;
  related_package_id: string | null;
  created_at: string;
  updated_at: string;
  clients?: {
    id: string;
    name_ar: string;
    mobile: string | null;
  };
  employees?: {
    id: string;
    name: string;
  };
}

export const useClientFollowUps = (employeeId?: string) => {
  return useQuery({
    queryKey: ["client-follow-ups", employeeId],
    queryFn: async () => {
      let query = supabase
        .from("client_follow_ups")
        .select(`
          *,
          clients(id, name_ar, mobile),
          employees(id, name)
        `)
        .order("created_at", { ascending: false });

      if (employeeId) {
        query = query.eq("employee_id", employeeId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as ClientFollowUp[];
    },
  });
};

export const useCreateFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (followUp: any) => {
      const { data, error } = await supabase
        .from("client_follow_ups")
        .insert([followUp])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-follow-ups"] });
      toast.success("تم إنشاء المتابعة بنجاح");
    },
    onError: (error) => {
      toast.error("فشل في إنشاء المتابعة: " + error.message);
    },
  });
};

export const useUpdateFollowUp = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<ClientFollowUp> & { id: string }) => {
      const { data, error } = await supabase
        .from("client_follow_ups")
        .update(updates)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["client-follow-ups"] });
      toast.success("تم تحديث المتابعة بنجاح");
    },
    onError: (error) => {
      toast.error("فشل في تحديث المتابعة: " + error.message);
    },
  });
};

export const useFollowUpStats = (employeeId?: string, startDate?: string, endDate?: string) => {
  return useQuery({
    queryKey: ["followup-stats", employeeId, startDate, endDate],
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_followup_stats", {
        p_employee_id: employeeId,
        p_start_date: startDate,
        p_end_date: endDate,
      });

      if (error) throw error;
      return data;
    },
    enabled: !!employeeId,
  });
};